import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  BookingTransaction,
  BookingTransactionService,
} from 'us-web-services';
import {
  getFullDateString,
  getTimeOfDayString,
} from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const URBANSITTER_LOGO =
  'https://assets.urbansitter.com/static/img/logos/urbansitter_logo_color.svg';

const formatCurrency = (n?: number | null): string => {
  if (n == null) return '$0.00';
  return `$${Number(n).toFixed(2)}`;
};

// CSV exports use a unique "MM-DD-YYYY" dash separator, distinct from the rest
// of the app's "MM/DD/YYYY" slash format.
const formatIsoDate = (iso?: string | Date): string => {
  if (!iso) return '';
  const d = new Date(iso as string);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
};

interface AddressLike {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

const addressString = (a?: AddressLike): string => {
  if (!a) return '';
  return `${a.street || ''}, ${a.city || ''}, ${a.state || ''} ${a.zip || ''}`;
};

interface KidLike {
  name?: string;
}

const childrenNames = (kids?: KidLike[], typeId?: number): string => {
  if (typeId !== 1 || !kids) return '';
  return kids.map((k) => k.name || '').filter(Boolean).join(', ');
};

export const ChildcareTransactions = observer((): JSX.Element => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const parent = stores.parent.parent;

  const isParent = !!user?.parent;
  const profile = isParent ? parent : (stores as unknown as { sitter?: { sitter?: unknown } }).sitter;
  const userCreated = (profile as unknown as { userStats?: { created?: string } } | undefined)?.userStats?.created;

  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [transactions, setTransactions] = useState<{
    data?: BookingTransaction[];
    meta?: { size?: number; pageCount?: number };
  } | null>(null);
  const [page, setPage] = useState<number>(Number(searchParams.get('page') || 1) || 1);
  const [exporting, setExporting] = useState(false);

  const currentDateString = useMemo(() => getFullDateString(new Date()), []);

  useEffect(() => {
    document.title = 'UrbanSitter - Childcare Transactions';
  }, []);

  const filterYears = useMemo(() => {
    if (!userCreated) return [new Date().getFullYear()];
    const yearCreated = new Date(String(userCreated).replace(/-/g, '/')).getFullYear();
    const yearCurrent = new Date().getFullYear();
    const ys: number[] = [];
    for (let i = yearCurrent; i >= yearCreated; i--) ys.push(i);
    return ys;
  }, [userCreated]);

  const loadPage = async (nextPage: number, limit = 10): Promise<void> => {
    if (!user?.id) return;
    const params: Record<string, unknown> = {
      userId: user.id,
      parentId: user.parent ? user.id : null,
      sitterId: user.sitter ? user.id : null,
      filterType: 'all_date',
      sort: 'startDate',
      'startDate.dir': 'desc',
      limit,
      page: nextPage,
    };
    if (selectedYear) {
      const year = +selectedYear;
      if (selectedMonth) {
        const month = +selectedMonth;
        const monthStart = String(month).padStart(2, '0');
        const monthEnd = month === 12 ? '01' : String(month + 1).padStart(2, '0');
        const yearStart = year;
        const yearEnd = month === 12 ? year + 1 : year;
        params.start = `${yearStart}-${monthStart}-01 00:00:00`;
        params.end = `${yearEnd}-${monthEnd}-01 00:00:00`;
      } else {
        params.start = `${year}-01-01 00:00:00`;
        params.end = `${year + 1}-01-01 00:00:00`;
      }
    }
    try {
      const resp = (await BookingTransactionService.getByFilter({ params })).data;
      setTransactions({ data: resp?.data ?? [], meta: resp?.meta });
    } catch (err) {
      console.warn('[ChildcareTransactions] load failed', err);
    }
  };

  useEffect(() => {
    loadPage(page);

  }, [user?.id, selectedMonth, selectedYear]);

  const verticalLabel = (typeId?: number): string => {
    if (!typeId) return '';
    const key = `pages.childcareTransactions.vertical.${typeId}`;
    const out = t(key);
    return out === key ? '' : out;
  };

  const download = (filename: string, type: string, text: string): void => {
    const el = document.createElement('a');
    el.setAttribute('href', type + encodeURIComponent(text));
    el.setAttribute('download', filename);
    el.style.display = 'none';
    document.body.appendChild(el);
    el.click();
    document.body.removeChild(el);
  };

  const exportCsv = async (): Promise<void> => {
    if (!selectedYear) return;
    setExporting(true);
    try {
      // Export up to 2000 rows, matching Angular behavior.
      await loadPage(1, 2000);
      const list = transactions?.data ?? [];
      const headers: string[] = [
        'Family Name',
        'Caregiver Name',
        'Date',
        'Start Time',
        'End Time',
        'Location',
        'Type',
        'Services Provided',
        "Children's Names",
        'Payment Type',
        'Rate',
        'Hours',
        'Service Total',
        'Tip',
      ];
      if (user?.parent) {
        headers.push('Platform Fee');
        headers.push('Credit Applied');
      }
      headers.push('Total Paid');
      headers.push('Caregiver SSN');

      const rows: string[] = [headers.join(',')];
      for (const tx of list) {
        const booking = tx.booking;
        const row: Array<string | number | null | undefined> = [
          booking?.parent?.name,
          booking?.caregiver?.firstName,
          formatIsoDate(booking?.start),
          getTimeOfDayString(booking?.start),
          getTimeOfDayString(booking?.end),
          addressString(booking?.address as AddressLike),
          booking?.type,
          verticalLabel(booking?.typeId),
          childrenNames(booking?.parent?.kids as KidLike[] | undefined, booking?.typeId),
          booking?.paymentType,
          booking?.rate,
          tx.totalHours,
          tx.subtotal,
          booking?.tip,
        ];
        if (user?.parent) {
          row.push(tx.platformFee);
          row.push(tx.discount ? `-${tx.discount}` : 0);
        }
        row.push(tx.totalPaid);
        row.push(null);
        rows.push(row.map((c) => (c ? `"${c}"` : '')).join(','));
      }
      const filename = `${+selectedYear} UrbanSitter Transactions.csv`;
      download(filename, 'data:text/csv;charset=utf-8,', rows.join('\n'));
    } finally {
      setExporting(false);
    }
  };

  const totalPages = transactions?.meta?.pageCount ?? 1;
  const showPager = !!transactions?.meta && totalPages > 1;

  return (
    <div className='childcare-transactions pt-8 px-6'>
      <div className='container'>
        <img
          className='block mx-auto mb-2'
          src={URBANSITTER_LOGO}
          alt='UrbanSitter logo'
          width={213}
          height={40}
        />
        <div className='text-xs text-center mb-4'>
          {t('pages.childcareTransactions.disclaimerStart')}{' '}
          {user?.parent && (
            <>
              {t('pages.childcareTransactions.disclaimerParent')}{' '}
            </>
          )}
          {t('pages.childcareTransactions.disclaimerEnd')}
        </div>

        <h1 className='text-2xl font-semibold'>{t('pages.childcareTransactions.heading')}</h1>
        <hr className='my-3' />

        <div className='flex flex-wrap gap-4 mb-3'>
          <div className='flex gap-2'>
            <select
              name='month'
              id='month'
              className='border border-neutral-300 rounded px-2 py-1'
              value={selectedMonth}
              onChange={(e): void => setSelectedMonth(e.target.value)}
            >
              <option value=''>{t('pages.childcareTransactions.allMonths')}</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {t(`pages.childcareTransactions.months.${m}`)}
                </option>
              ))}
            </select>
            <select
              name='year'
              id='year'
              className='border border-neutral-300 rounded px-2 py-1'
              value={selectedYear}
              onChange={(e): void => setSelectedYear(e.target.value)}
            >
              <option value=''>{t('pages.childcareTransactions.allYears')}</option>
              {filterYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <Button
              buttonType={ButtonType.primary}
              onClick={exportCsv}
              disabled={!selectedYear || exporting}
              className='px-5 py-2'
            >
              {!exporting && t('pages.childcareTransactions.exportCta')}
              {exporting && (
                <span>
                  <i className='fa-solid fa-spinner fa-spin mr-2' />
                  {t('pages.childcareTransactions.exporting')}
                </span>
              )}
            </Button>
            <br />
            {!selectedYear && (
              <small className='text-neutral-500'>
                {t('pages.childcareTransactions.yearRequired')}
              </small>
            )}
          </div>
        </div>
        <hr className='my-3' />
      </div>

      {!transactions && (
        <div className='text-center py-4'>
          <i className='fa-solid fa-spinner fa-spin mt-2' />{' '}
          <span>{t('pages.childcareTransactions.loading')}</span>
        </div>
      )}

      <div className='overflow-x-auto'>
        <table className='mb-4 w-full text-sm'>
          <thead>
            <tr>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.family')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.provider')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.date')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.start')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.end')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.location')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.type')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.services')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.children')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.paymentType')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.rate')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.hours')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.serviceTotal')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.tip')}</th>
              {user?.parent && (
                <>
                  <th className='text-left px-2'>{t('pages.childcareTransactions.col.platformFee')}</th>
                  <th className='text-left px-2'>{t('pages.childcareTransactions.col.creditApplied')}</th>
                </>
              )}
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.totalPaid')}</th>
              <th className='text-left px-2'>{t('pages.childcareTransactions.col.providerSsn')}</th>
            </tr>
          </thead>
          <tbody>
            {(transactions?.data ?? []).map((tx, i) => (
              <tr
                key={tx.id}
                className={i % 2 === 0 ? 'bg-neutral-100' : 'bg-white'}
              >
                <td className='px-2'>{tx.booking?.parent?.name}</td>
                <td className='px-2'>{tx.booking?.caregiver?.name}</td>
                <td className='px-2'>{formatIsoDate(tx.booking?.start)}</td>
                <td className='px-2'>{getTimeOfDayString(tx.booking?.start)}</td>
                <td className='px-2'>{getTimeOfDayString(tx.booking?.end)}</td>
                <td className='px-2 text-xs'>{addressString(tx.booking?.address as AddressLike)}</td>
                <td className='px-2'>{tx.booking?.type}</td>
                <td className='px-2'>{verticalLabel(tx.booking?.typeId)}</td>
                <td className='px-2'>
                  {childrenNames(tx.booking?.parent?.kids as KidLike[] | undefined, tx.booking?.typeId)}
                </td>
                <td className='px-2'>{tx.booking?.paymentType}</td>
                <td className='px-2'>{formatCurrency(tx.booking?.rate)}</td>
                <td className='px-2'>{tx.totalHours}</td>
                <td className='px-2'>{formatCurrency(tx.subtotal)}</td>
                <td className='px-2'>{formatCurrency(tx.booking?.tip)}</td>
                {user?.parent && (
                  <>
                    <td className='px-2'>{formatCurrency(tx.platformFee)}</td>
                    <td className='px-2'>
                      {tx.discount ? `-${formatCurrency(tx.discount)}` : '$0'}
                    </td>
                  </>
                )}
                <td className='px-2'>{formatCurrency(tx.totalPaid)}</td>
                <td className='px-2' />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showPager && (
        <div className='text-center mb-3'>
          <Button
            buttonType={ButtonType.secondary}
            onClick={(): void => {
              if (page > 1) {
                setPage(page - 1);
                loadPage(page - 1);
              }
            }}
            disabled={page <= 1}
          >
            Previous
          </Button>{' '}
          Page {page} of {totalPages}{' '}
          <Button
            buttonType={ButtonType.secondary}
            onClick={(): void => {
              if (page < totalPages) {
                setPage(page + 1);
                loadPage(page + 1);
              }
            }}
            disabled={page >= totalPages}
          >
            Next
          </Button>
        </div>
      )}

      <div className='text-xs text-center mb-8'>
        {currentDateString}
        {' - '}
        {(profile as { firstName?: string })?.firstName}{' '}
        {(profile as { lastName?: string })?.lastName}
      </div>
    </div>
  );
});
